import { createContext, useContext, useEffect, useState } from 'react';
import { Merchant, MerchantsResponse, MerchantResponse, MerchantTypeResponse, ExecutiveResponse, Executive, Type, MerchantIdTypeResponse, TokenResponse } from '../../models/interfaces';
import topApi from '../../components/api/ToPApi';
import { AuthContext } from '../auth';




type MerchantsContextProps = {
    merchants: Merchant[];
    totalPage: number;
    isLoading: boolean;
    loadMerchants: (page: number) => Promise<void>;
    loadAllMerchants: (page: number) => Promise<void>;
    loadMerchantsInScope: () => Promise<void>;
    addMerchant: (merchant: Merchant) => Promise<string>;
    updateMerchant: (merchant: Merchant) => Promise<string>;
    deleteMerchant: (id: number) => Promise<string>;
    suspendMerchant: (id: number) => Promise<string>;
    // 'success', el error, o el resultado del cobro de la suscripción si estaba suspendida por falta de pago
    activateMerchant: (id: number) => Promise<string | { subscription: { outcome: string; message: string } }>;
    getWithoutITBMSConfiguration: () => Promise<Merchant[]>;
    configMerchant: (merchant: any) => Promise<string>;
    loadMerchantTypes: (countryId?: number | null) => Promise<Type[]>;
    loadExecutives: () => Promise<Executive[]>;
    loadMerchantIdTypes: (countryId?: number | null) => Promise<Type[]>;
    generateTokenEcom: (id: number) => Promise<string>;
    getCurrentMerchant: () => Promise<Merchant | null>;



}



export const MerchantsContext = createContext({} as MerchantsContextProps);

export const MerchantsProvider = ({ children }: any) => {

    const [merchants, setMerchants] = useState<Merchant[]>([]);
    const [totalPage, setTotalPage] = useState<number>(0);
    const [isLoading, setIsLoading] = useState(false);

    const { user } = useContext(AuthContext);
    const pageSize = 15;

    // Catálogos por país (Costa Rica si no se indica)
    const loadMerchantTypes = async (countryId?: number | null) => {

        try {
            setIsLoading(true);
            const resp = await topApi.get<MerchantTypeResponse>(`/merchant/getMerchantTypes${countryId ? `?countryId=${countryId}` : ''}`);
            if (resp.status == 200) {
                return resp.data.types;
            }
            setIsLoading(false);
        } catch (error) {
            return []
        }
    }

    const loadMerchantIdTypes = async (countryId?: number | null) => {

        try {
            setIsLoading(true);
            const resp = await topApi.get<MerchantIdTypeResponse>(`/merchant/getMerchantIdTypes${countryId ? `?countryId=${countryId}` : ''}`);
            if (resp.status == 200) {
                return resp.data.idTypes;
            }
            setIsLoading(false);
        } catch (error) {
            return []
        }
    }



    const loadExecutives = async () => {

        try {
            setIsLoading(true);
            const resp = await topApi.get<ExecutiveResponse>('/merchant/getExecutives');
            if (resp.status == 200) {
                return resp.data.executives;
            }
            setIsLoading(false);
        } catch (error) {
            return []
        }
    }

    const loadMerchants = async (pageActual) => {

        try {
            setIsLoading(true);
            const resp = await topApi.get<MerchantsResponse>('/merchant/getMerchants/' + pageActual + '/' + pageSize);
            if (resp.status == 200) {
                setMerchants([...resp.data.merchants]);
                setTotalPage(resp.data.pages);
            }
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
        }
    }

    // Comercios del alcance del usuario (plataforma: todos; banco: los de su banco; comercio: el suyo)
    const loadMerchantsInScope = async () => {
        try {
            setIsLoading(true);
            const resp = await topApi.get<MerchantsResponse>('/merchant/scope');
            setMerchants([...(resp.data.merchants ?? [])]);
        } catch {
            setMerchants([]);
        } finally {
            setIsLoading(false);
        }
    }

    const loadAllMerchants = async () => {

        try {
            setIsLoading(true);
            const resp = await topApi.get<MerchantsResponse>('/merchant/getMerchants/' + 0 + '/' + 1000);
            if (resp.status == 200) {
                setMerchants([...resp.data.merchants]);
                setTotalPage(resp.data.pages);
            }
            setIsLoading(false);
        } catch (error) {
            // Los usuarios de comercio no ven la lista de comercios (403): solo consultan el suyo
            setMerchants([]);
            setIsLoading(false);
        }
    }



    const addMerchant = async (merchant: Merchant) => {
        try {
            setIsLoading(true);
            // merchant.contract_code = ''+merchant.contract_code;
            // merchant.terminal_id = ''+merchant.terminal_id;
            // merchant.category_code = ''+merchant.category_code;
            merchant.zip_code = '' + merchant.zip_code;
            merchant.id = undefined;
            merchant.topId = merchant.terminal_id;
            merchant.topIdDol = merchant.terminal_id_dol;

            const resp = await topApi.post<MerchantResponse>('/merchant/', { merchant });
            setMerchants([...merchants, resp.data.merchant]);
            setIsLoading(false);
            return 'success'

        } catch (e) {
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }
    }


    const updateMerchant = async (merchant: Merchant) => {
        try {
            setIsLoading(true);

            merchant.zip_code = '' + merchant.zip_code;
            merchant.topId = merchant.terminal_id;
            merchant.topIdDol = merchant.terminal_id_dol;

            const resp = await topApi.put<Merchant>(`/merchant/${merchant.id}`, { merchant });

            setMerchants(merchants.map(prod => {
                return (prod.id === merchant.id)
                    ? resp.data["merchant"]
                    : prod;
            }));
            setIsLoading(false);
            return 'success'

        } catch (e) {
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }


    }

    const deleteMerchant = async (id: number) => {
        try {
            setIsLoading(true);
            await topApi.delete<Merchant>(`/merchant/${id}`);
            setMerchants((prevMerchants) =>
                prevMerchants.filter((merchant) => merchant.id! !== id)
            );
            setIsLoading(false);
            return 'success'
        } catch (e) {
            setIsLoading(false);
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }
    }

    const suspendMerchant = async (id: number) => {
        try {
            setIsLoading(true);
            await topApi.post<Merchant>(`/merchant/suspend/${id}`);
            setMerchants(merchants.map(prod => {
                if (prod.id === id) {
                    prod.status = 'INA'
                }
                return prod;
            }));
            setIsLoading(false);
            return 'success'
        } catch (e) {
            setIsLoading(false);
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }
    }

    const activateMerchant = async (id: number) => {
        try {
            setIsLoading(true);
            const resp = await topApi.post<{ subscription?: { outcome: string; message: string } | null }>(`/merchant/activate/${id}`);
            setMerchants(merchants.map(prod => {
                if (prod.id === id) {
                    prod.status = 'ACT'
                }
                return prod;
            }));
            setIsLoading(false);
            return resp.data?.subscription ? { subscription: resp.data.subscription } : 'success'
        } catch (e) {
            setIsLoading(false);
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }
    }

    const getWithoutITBMSConfiguration = async () => {

        try {
            const resp = await topApi.get<MerchantsResponse>('/merchant/getWithoutITBMSConfiguration');
            if (resp.status == 200) {
                return resp.data.merchants;
            }
            return [];
        } catch (error) {

        }
    }




    const getCurrentMerchant = async () => {

        try {
            setIsLoading(true);
            const resp = await topApi.get<MerchantResponse>('/merchant/getCurrentMerchant');
            if (resp.status == 200) {
                return resp.data.merchant;
            }
            setIsLoading(false);
            return null;
        } catch (error) {

            setIsLoading(false);
            return null;
        }
    }





    const configMerchant = async (merchant: any) => {
        try {
            setIsLoading(true);

            const resp = await topApi.put<Merchant>(`/merchant/config/${merchant.id}`, { merchant });

            setMerchants(merchants.map(prod => {
                return (prod.id === merchant.id)
                    ? resp.data["merchant"]
                    : prod;
            }));

            user.merchant.image = resp.data["merchant"].image;

            setIsLoading(false);
            return 'success'

        } catch (e) {
            setIsLoading(false);
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }


    }

    const generateTokenEcom = async (id: number) => {
        try {
            setIsLoading(true);
            const data = {
                merchant_id: id
            }
            const resp = await topApi.post<TokenResponse>(`/security/generateTokenDLL`, data);

            setIsLoading(false);
            return resp.data.token;
        } catch (e) {
            setIsLoading(false);
            return "fail";
        }
    }

    return (
        <MerchantsContext.Provider value={{
            merchants, totalPage, loadMerchants, addMerchant, updateMerchant, deleteMerchant,
            suspendMerchant, activateMerchant, isLoading, getWithoutITBMSConfiguration, configMerchant, loadMerchantTypes,
            loadExecutives, loadMerchantIdTypes, loadAllMerchants, loadMerchantsInScope, generateTokenEcom, getCurrentMerchant
        }}>
            {children}
        </MerchantsContext.Provider>
    );
}